// QR scan resolution — the CLIENT side of the platform QR canon. A floor
// scanner acts as a keyboard-wedge: it types the decoded value into the
// focused input, and these parsers turn whatever lands there into a typed
// target, or `undefined` when they cannot (the screen then shows the
// unreadable-code hint). Resolution is tenant-scoped by the subdomain the PWA
// runs on, so a foreign/unknown id simply resolves to "not found" downstream —
// no parser here ever signals cross-tenant existence.
//
// Three path kinds are recognised client-side:
//   /q/p/{uuid}  — product card (Module 01 §3.1, "Scan resolution (S9)").
//   /q/wc/{uuid} — WORK-CENTRE station sheet (spec 02 §2.9's print kit; the
//                  resolver spec 06 §3.5's recorded-gap paragraph owed to
//                  modules 16/04, shipped 04-B-3): scanning a station sticker
//                  means "pin this device to this station" — a module-04 floor
//                  concern, NOT a stock lookup, which is why the inventory
//                  scan ENDPOINT deliberately keeps answering
//                  `resolved_kind='unknown'` for it (M02-S05 pins that
//                  contract).
//   /q/op/{uuid} — traveler per-op QR (spec 04 §5 row 29): a shortcut into
//                  that op's run screen; the station QR remains the context
//                  anchor.
// /q/su/ and /q/loc/ payloads are DELIBERATELY not parsed here: §9.9 — the
// client never resolves a stock payload; they ride raw to the server's scan
// endpoint.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// The canonical scan path segments, tolerant of a trailing slash or query/hash
// the scanner might append.
const QR_PATH_RE = /\/q\/p\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
const WC_PATH_RE = /\/q\/wc\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
const OP_PATH_RE = /\/q\/op\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Any platform scan payload, whatever kind of thing it points at, plus the bare
 * URL shape a scanner emits — `…/q/{su|loc|p|wc|op}/{uuid}` / `https://…`.
 *
 * This is a SHAPE test, not a resolution: §9.9 still stands, and no caller
 * learns from it what the payload points at or whether it exists. It answers
 * one question a form needs — "is this string a scanned label rather than
 * something a person typed?" — because a wedge scanner fires into whichever
 * field a thumb last touched, so the field a string arrives in proves nothing
 * about what it is. A laser engraves a serial into steel; nothing engraves a
 * URL. Mirrors the server's own `SerialLotScanResolver` rule (04-C-3 fix,
 * 2026-08-06), and the mirror matters offline: a tap keyed at the machine may
 * not reach the server for hours, so the form must not queue a capture the
 * server will only refuse at sync.
 */
const SCAN_PAYLOAD_RE =
  /(\/q\/[a-z]+\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|^https?:\/\//i;

export function looksLikeScanPayload(raw: string): boolean {
  return SCAN_PAYLOAD_RE.test(raw.trim());
}

/**
 * Extract a product id from a scanned value. Accepts the full canonical URL, the
 * `/q/p/{uuid}` path alone, or a bare uuid. Returns `undefined` for anything
 * that does not contain a well-formed product id.
 */
export function parseScanValue(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;

  const pathMatch = QR_PATH_RE.exec(trimmed);
  if (pathMatch !== null) return pathMatch[1]?.toLowerCase();

  if (UUID_RE.test(trimmed)) return trimmed.toLowerCase();

  return undefined;
}

/** A scanned value the floor client can act on WITHOUT the server. */
export type FloorScanTarget =
  | { readonly kind: "product"; readonly id: string }
  | { readonly kind: "work_center"; readonly id: string }
  | { readonly kind: "operation"; readonly id: string };

/**
 * Classify a scanned value into the typed target the floor router acts on:
 * `/q/wc/{uuid}` pins the station (S9), `/q/op/{uuid}` opens that op's run
 * screen, `/q/p/{uuid}` opens the product card.
 *
 * A BARE uuid is `undefined` here — with no path segment there is nothing to
 * say which kind it is, and guessing "station" would bind a device to a
 * product id. (The product lookup keeps its bare-uuid tolerance in
 * `parseScanValue`, where the question is unambiguous.) Anything else —
 * including `/q/su/` and `/q/loc/` payloads, which the server resolves — is
 * also `undefined`.
 */
export function parseFloorScan(raw: string): FloorScanTarget | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;

  const wc = WC_PATH_RE.exec(trimmed);
  if (wc?.[1] !== undefined) return { kind: "work_center", id: wc[1].toLowerCase() };

  const op = OP_PATH_RE.exec(trimmed);
  if (op?.[1] !== undefined) return { kind: "operation", id: op[1].toLowerCase() };

  const product = QR_PATH_RE.exec(trimmed);
  if (product?.[1] !== undefined) return { kind: "product", id: product[1].toLowerCase() };

  return undefined;
}
